import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createPunchOnBehalf, getCostCodes, getCustomers, getUsers, type Entry } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";

export function useOnBehalfViewModel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { canCreateOnBehalf, isManagerOnly, isAdmin, canEditPunches } = useDashboardRoles();
  const [onBehalfOpen, setOnBehalfOpen] = React.useState(false);
  const [createdPunches, setCreatedPunches] = React.useState<Entry[]>([]);
  const [onBehalfForm, setOnBehalfForm] = React.useState({
    employeeId: "",
    employeeLabel: "",
    clockIn: "",
    clockOut: "",
    costCodeId: "",
    costCodeLabel: "",
    customerId: "",
    customerLabel: "",
    notes: "",
    error: "",
  });
  const [obEmployeeSearch, setObEmployeeSearch] = React.useState("");
  const [obEmployeeOpen, setObEmployeeOpen] = React.useState(false);
  const obEmployeeRef = React.useRef<HTMLDivElement>(null);
  const [obCostCodeSearch, setObCostCodeSearch] = React.useState("");
  const [obCostCodeOpen, setObCostCodeOpen] = React.useState(false);
  const obCostCodeRef = React.useRef<HTMLDivElement>(null);
  const [obCustomerSearch, setObCustomerSearch] = React.useState("");
  const [obCustomerOpen, setObCustomerOpen] = React.useState(false);
  const obCustomerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (obEmployeeRef.current && !obEmployeeRef.current.contains(event.target as Node)) setObEmployeeOpen(false);
      if (obCostCodeRef.current && !obCostCodeRef.current.contains(event.target as Node)) setObCostCodeOpen(false);
      if (obCustomerRef.current && !obCustomerRef.current.contains(event.target as Node)) setObCustomerOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: onBehalfUsersData } = useQuery({
    queryKey: ["users-on-behalf", obEmployeeSearch],
    queryFn: () =>
      getUsers({
        include_inactive: false,
        search: obEmployeeSearch.trim() || undefined,
        limit: 100,
        offset: 0,
      }),
    enabled: !!user && canCreateOnBehalf && onBehalfOpen,
  });
  const { data: onBehalfCostCodesData } = useQuery({
    queryKey: ["cost-codes-on-behalf", obCostCodeSearch],
    queryFn: () => getCostCodes(obCostCodeSearch),
    enabled: !!user && canCreateOnBehalf && onBehalfOpen,
  });
  const { data: onBehalfCustomersData } = useQuery({
    queryKey: ["customers-on-behalf", obCustomerSearch],
    queryFn: () => getCustomers(obCustomerSearch),
    enabled: !!user && canCreateOnBehalf && onBehalfOpen,
  });

  const resetOnBehalfForm = React.useCallback(() => {
    setOnBehalfForm({
      employeeId: "",
      employeeLabel: "",
      clockIn: "",
      clockOut: "",
      costCodeId: "",
      costCodeLabel: "",
      customerId: "",
      customerLabel: "",
      notes: "",
      error: "",
    });
    setObEmployeeSearch("");
    setObEmployeeOpen(false);
    setObCostCodeSearch("");
    setObCostCodeOpen(false);
    setObCustomerSearch("");
    setObCustomerOpen(false);
  }, []);

  const createOnBehalfMutation = useMutation({
    mutationFn: createPunchOnBehalf,
    onSuccess: async (created) => {
      const labeled: Entry = {
        ...created,
        employee_name: created.employee_name || onBehalfForm.employeeLabel || created.employee_name,
        cost_code_name: created.cost_code_name || onBehalfForm.costCodeLabel || created.cost_code_name,
        customer_name: created.customer_name || onBehalfForm.customerLabel || created.customer_name,
      };
      setCreatedPunches((prev) => [labeled, ...prev.filter((e) => e.id !== labeled.id)]);
      setOnBehalfOpen(false);
      resetOnBehalfForm();
      await queryClient.invalidateQueries({ queryKey: ["entries"] });
      await queryClient.invalidateQueries({ queryKey: ["recent-entries"] });
      await queryClient.invalidateQueries({ queryKey: ["entries-count"] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Could not create punch.";
      setOnBehalfForm((f) => ({ ...f, error: msg }));
    },
  });


  return {
    user, isAdmin, isManagerOnly, canCreateOnBehalf, canEditPunches,
    onBehalfOpen, setOnBehalfOpen,
    onBehalfForm, setOnBehalfForm,
    obEmployeeSearch, setObEmployeeSearch, obEmployeeOpen, setObEmployeeOpen, obEmployeeRef,
    obCostCodeSearch, setObCostCodeSearch, obCostCodeOpen, setObCostCodeOpen, obCostCodeRef,
    obCustomerSearch, setObCustomerSearch, obCustomerOpen, setObCustomerOpen, obCustomerRef,
    onBehalfUsersData, onBehalfCostCodesData, onBehalfCustomersData,
    resetOnBehalfForm, createOnBehalfMutation,
    createdPunches,
  };
}
